import React, { useState, useEffect } from 'react';
import { createPledge } from '../../services/pledgeService';
import { getCachedData, clearCachedData } from '../../services/cacheService';

const PledgeForm = ({ onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    title: '',
    user_id: '',
    charity_id: '',
    amount_cents: '',
    duration_weeks: '',
    visits_per_week: '',
    payment_method_id: 'pm_card_visa',
    location_address: {
      street_name: '',
      house_number: '',
      post_code: '',
      city: '',
      country: '',
      latitude: '',
      longitude: ''
    },
    start_date: '',
    end_date: '',
    time_zone: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [users, setUsers] = useState([]);
  const [charities, setCharities] = useState([]);

  useEffect(() => {
    // Load users and charities from cache
    const cachedUsers = getCachedData('users');
    const cachedCharities = getCachedData('charities');
    
    if (cachedUsers && cachedUsers.data && cachedUsers.data.users) {
      setUsers(cachedUsers.data.users);
    }
    
    if (cachedCharities && cachedCharities.data && cachedCharities.data.charities) {
      setCharities(cachedCharities.data.charities);
    }

    // Set default start date to today and detect timezone
    const today = new Date().toISOString().split('T')[0];
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    setFormData(prev => ({
      ...prev,
      start_date: today,
      time_zone: userTimeZone
    }));
  }, []);

  // Calculate end date when start date or duration changes
  useEffect(() => {
    if (formData.start_date && formData.duration_weeks) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + (parseInt(formData.duration_weeks) * 7));
      
      setFormData(prev => ({
        ...prev,
        end_date: endDate.toISOString().split('T')[0]
      }));
    }
  }, [formData.start_date, formData.duration_weeks]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title || formData.title.trim() === '') {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.user_id) {
      newErrors.user_id = 'Please select a user';
    }
    
    if (!formData.charity_id) {
      newErrors.charity_id = 'Please select a charity';
    }
    
    if (!formData.amount_cents || formData.amount_cents <= 0) {
      newErrors.amount_cents = 'Amount must be greater than 0';
    }
    
    if (!formData.duration_weeks || formData.duration_weeks <= 0) {
      newErrors.duration_weeks = 'Duration must be greater than 0';
    }
    
    if (!formData.visits_per_week || formData.visits_per_week <= 0) {
      newErrors.visits_per_week = 'Visits per week must be greater than 0';
    }

    if (!formData.payment_method_id || formData.payment_method_id.trim() === '') {
      newErrors.payment_method_id = 'Payment method ID is required';
    }

    // Location address validation is optional - will use 'na' for missing fields
    
    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    
    if (name.startsWith('location_address.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        location_address: {
          ...prev.location_address,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'number' ? parseInt(value, 10) || '' : value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      // Stripe-funds-segregation contract for POST /pledges/for-user:
      //  - user_id is the TARGET user (backend reads target_user_id from it)
      //  - currency is locked to "EUR"
      //  - payment_method_id must belong to the target user (admin supplies a
      //    Stripe test PaymentMethod, e.g. pm_card_visa, for dev testing)
      // The backend creates+captures the PaymentIntent and persists the pledge;
      // status/end_date are derived server-side and must not be sent.
      const pledgeData = {
        title: formData.title.trim(),
        user_id: formData.user_id,
        charity_id: formData.charity_id,
        amount_cents: Number(formData.amount_cents),
        currency: 'EUR',
        duration_weeks: Number(formData.duration_weeks),
        visits_per_week: Number(formData.visits_per_week),
        payment_method_id: formData.payment_method_id.trim(),
        location_address: {
          street_name: formData.location_address.street_name?.trim() || 'na',
          house_number: formData.location_address.house_number?.trim() || 'na',
          post_code: formData.location_address.post_code?.trim() || 'na',
          city: formData.location_address.city?.trim() || 'na',
          country: formData.location_address.country?.trim() || 'na',
          latitude: formData.location_address.latitude?.trim() || '0',
          longitude: formData.location_address.longitude?.trim() || '0'
        },
        start_date: formData.start_date,
        time_zone: formData.time_zone,
      };
      
      await createPledge(pledgeData);
      
      // Clear cache to ensure fresh data on next load
      clearCachedData('pledges');
      
      onSuccess('Pledge created successfully!');
    } catch (err) {
      console.error('Error creating pledge:', err);
      const raw = err.message || '';
      let message = 'Failed to create pledge. ' + (raw || 'Please try again.');
      if (raw.includes('STRIPE_ACCOUNT_INVALID')) {
        message = 'The selected charity is not Stripe-verified, so it cannot receive pledges. Verify the charity first.';
      } else if (raw.includes('PAYMENT_FAILED')) {
        message = 'The card was declined. Try a different Stripe test payment method.';
      }
      onError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label htmlFor="title" className="block text-sm font-medium text-gray-700">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="title"
            id="title"
            value={formData.title}
            onChange={handleChange}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
              errors.title ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="e.g., Visit Animal Shelter Weekly"
          />
          {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
        </div>

        <div>
          <label htmlFor="user_id" className="block text-sm font-medium text-gray-700">
            User <span className="text-red-500">*</span>
          </label>
          <select
            name="user_id"
            id="user_id"
            value={formData.user_id}
            onChange={handleChange}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
              errors.user_id ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            <option value="">Select a user</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.email})
              </option>
            ))}
          </select>
          {errors.user_id && <p className="mt-1 text-sm text-red-600">{errors.user_id}</p>}
        </div>

        <div>
          <label htmlFor="charity_id" className="block text-sm font-medium text-gray-700">
            Charity <span className="text-red-500">*</span>
          </label>
          <select
            name="charity_id"
            id="charity_id"
            value={formData.charity_id}
            onChange={handleChange}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
              errors.charity_id ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            <option value="">Select a charity</option>
            {charities.map(charity => (
              <option key={charity.id} value={charity.id}>
                {charity.name}
              </option>
            ))}
          </select>
          {errors.charity_id && <p className="mt-1 text-sm text-red-600">{errors.charity_id}</p>}
        </div>

        <div className="md:col-span-2">
          <label htmlFor="payment_method_id" className="block text-sm font-medium text-gray-700">
            Stripe Payment Method ID <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="payment_method_id"
            id="payment_method_id"
            value={formData.payment_method_id}
            onChange={handleChange}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
              errors.payment_method_id ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="pm_card_visa"
          />
          {errors.payment_method_id && <p className="mt-1 text-sm text-red-600">{errors.payment_method_id}</p>}
          <p className="mt-1 text-sm text-gray-500">
            This creates a real Stripe charge against the target user. For test
            mode use a Stripe test PaymentMethod such as <code>pm_card_visa</code>.
            The charity must be Stripe-verified for the charge to succeed.
          </p>
        </div>

        <div>
          <label htmlFor="amount_cents" className="block text-sm font-medium text-gray-700">
            Amount (Cents) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            name="amount_cents"
            id="amount_cents"
            value={formData.amount_cents}
            onChange={handleChange}
            min="1"
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
              errors.amount_cents ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="e.g., 5000 (for $50.00)"
          />
          {errors.amount_cents && <p className="mt-1 text-sm text-red-600">{errors.amount_cents}</p>}
          {formData.amount_cents && (
            <p className="mt-1 text-sm text-gray-500">
              Amount: ${(formData.amount_cents / 100).toFixed(2)}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="duration_weeks" className="block text-sm font-medium text-gray-700">
            Duration (Weeks) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            name="duration_weeks"
            id="duration_weeks"
            value={formData.duration_weeks}
            onChange={handleChange}
            min="1"
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
              errors.duration_weeks ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="e.g., 4"
          />
          {errors.duration_weeks && <p className="mt-1 text-sm text-red-600">{errors.duration_weeks}</p>}
        </div>

        <div>
          <label htmlFor="visits_per_week" className="block text-sm font-medium text-gray-700">
            Visits per Week <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            name="visits_per_week"
            id="visits_per_week"
            value={formData.visits_per_week}
            onChange={handleChange}
            min="1"
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
              errors.visits_per_week ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="e.g., 3"
          />
          {errors.visits_per_week && <p className="mt-1 text-sm text-red-600">{errors.visits_per_week}</p>}
        </div>

        <div>
          <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
            Start Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            name="start_date"
            id="start_date"
            value={formData.start_date}
            onChange={handleChange}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
              errors.start_date ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          {errors.start_date && <p className="mt-1 text-sm text-red-600">{errors.start_date}</p>}
        </div>

        <div>
          <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
            End Date (Calculated)
          </label>
          <input
            type="date"
            name="end_date"
            id="end_date"
            value={formData.end_date}
            readOnly
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 sm:text-sm"
          />
          <p className="mt-1 text-sm text-gray-500">
            Automatically calculated based on start date and duration
          </p>
        </div>

        <div>
          <label htmlFor="time_zone" className="block text-sm font-medium text-gray-700">
            Time Zone (Auto-detected)
          </label>
          <input
            type="text"
            name="time_zone"
            id="time_zone"
            value={formData.time_zone}
            readOnly
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 sm:text-sm"
          />
          <p className="mt-1 text-sm text-gray-500">
            Automatically detected from your browser settings
          </p>
        </div>

        <div className="md:col-span-2">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Location Address</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="location_address.street_name" className="block text-sm font-medium text-gray-700">
                Street Name
              </label>
              <input
                type="text"
                name="location_address.street_name"
                id="location_address.street_name"
                value={formData.location_address.street_name}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="e.g., Main Street"
              />
            </div>

            <div>
              <label htmlFor="location_address.house_number" className="block text-sm font-medium text-gray-700">
                House Number
              </label>
              <input
                type="text"
                name="location_address.house_number"
                id="location_address.house_number"
                value={formData.location_address.house_number}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="e.g., 123"
              />
            </div>

            <div>
              <label htmlFor="location_address.post_code" className="block text-sm font-medium text-gray-700">
                Post Code
              </label>
              <input
                type="text"
                name="location_address.post_code"
                id="location_address.post_code"
                value={formData.location_address.post_code}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="e.g., SW1A 1AA"
              />
            </div>

            <div>
              <label htmlFor="location_address.city" className="block text-sm font-medium text-gray-700">
                City
              </label>
              <input
                type="text"
                name="location_address.city"
                id="location_address.city"
                value={formData.location_address.city}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="e.g., London"
              />
            </div>

            <div>
              <label htmlFor="location_address.country" className="block text-sm font-medium text-gray-700">
                Country
              </label>
              <input
                type="text"
                name="location_address.country"
                id="location_address.country"
                value={formData.location_address.country}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="e.g., United Kingdom"
              />
            </div>

            <div>
              <label htmlFor="location_address.latitude" className="block text-sm font-medium text-gray-700">
                Latitude
              </label>
              <input
                type="text"
                name="location_address.latitude"
                id="location_address.latitude"
                value={formData.location_address.latitude}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="e.g., 51.5074"
              />
            </div>

            <div>
              <label htmlFor="location_address.longitude" className="block text-sm font-medium text-gray-700">
                Longitude
              </label>
              <input
                type="text"
                name="location_address.longitude"
                id="location_address.longitude"
                value={formData.location_address.longitude}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="e.g., -0.1278"
              />
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Location fields are optional. If left empty, default values will be used.
          </p>
        </div>

        {formData.duration_weeks && formData.visits_per_week && (
          <div className="md:col-span-2 bg-blue-50 p-4 rounded-md">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Pledge Summary</h4>
            <div className="text-sm text-blue-700 space-y-1">
              <p>Total visits required: {formData.duration_weeks * formData.visits_per_week}</p>
              <p>Duration: {formData.duration_weeks} weeks</p>
              <p>Frequency: {formData.visits_per_week} visits per week</p>
              {formData.amount_cents && (
                <p>Total amount: ${(formData.amount_cents / 100).toFixed(2)}</p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="submit"
          disabled={loading}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-6 rounded-lg transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating...' : 'Create Pledge'}
        </button>
      </div>
    </form>
  );
};

export default PledgeForm;